import { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { dollarsInputToCents, centsToInputValue } from "@/lib/money";
import { BundleFormFields } from "@/components/org/bundles/BundleFormFields";

export const BundleEditModal = ({ bundle, services, products, onClose, onSaved }) => {
  const { API } = useAuth();
  const [form, setForm] = useState({
    name: bundle.name || "",
    sku: bundle.sku || "",
    description: bundle.description || "",
    service_ids: bundle.service_ids || [],
    product_ids: bundle.product_ids || [],
    duration_minutes: bundle.duration_minutes != null ? String(bundle.duration_minutes) : "",
    durationTouched: true,
    price: bundle.price_cents != null ? centsToInputValue(bundle.price_cents) : "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!form.name.trim() || form.price === "") {
      setError("Bundle name and price are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        description: form.description.trim() || null,
        service_ids: form.service_ids,
        product_ids: form.product_ids,
        price_cents: dollarsInputToCents(form.price),
        duration_minutes: form.duration_minutes === "" ? null : parseInt(form.duration_minutes, 10),
      };
      const { data } = await axios.patch(`${API}/org/bundles/${bundle.bundle_id}`, payload, { withCredentials: true });
      onSaved(data.bundle);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save this bundle.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="bundle-edit-overlay">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="bundle-edit-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Edit Bundle</h2>
          <button type="button" onClick={onClose} data-testid="bundle-edit-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4">
          <BundleFormFields services={services} products={products} form={form} setForm={setForm} testIdPrefix="bundle-edit" />
        </div>
        {error && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="bundle-edit-error">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="bundle-edit-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="bundle-edit-save-btn" className="rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
};
